import { useState, useEffect, useCallback, useRef } from 'react';
import { GripVertical, Trash2, Loader2, AlertCircle } from 'lucide-react';
import { IconSparkles } from '@tabler/icons-react';
import { askQuestionStreaming, AskStreamEvent, AnswerMetadata } from '@/services/journalAIService';

interface StreamingNoteBlockProps {
  prompt: string;
  journalId: string;
  onStreamComplete?: (content: string, metadata?: AnswerMetadata) => void;
  onDelete?: () => void;
  dragHandleProps?: Record<string, unknown>;
}

// Fallback mock response for when API is unavailable
const getMockAIResponse = (prompt: string): string => {
  const lowerPrompt = prompt.toLowerCase();
  
  if (lowerPrompt.includes('photosynthesis')) {
    return `## Photosynthesis Explained

Photosynthesis is the remarkable process by which plants, algae, and some bacteria convert light energy into chemical energy stored in glucose.

### The Basic Equation

**6CO₂ + 6H₂O + Light Energy → C₆H₁₂O₆ + 6O₂**

### Key Stages

1. **Light-Dependent Reactions** (in thylakoid membranes)
   - Chlorophyll absorbs light energy
   - Water molecules are split, releasing oxygen
   - ATP and NADPH are produced

2. **Light-Independent Reactions** (Calvin Cycle)
   - CO₂ is fixed into organic compounds
   - Uses ATP and NADPH from stage 1
   - Produces glucose

### Why It Matters

Photosynthesis is the foundation of almost all life on Earth. It:
- Produces the oxygen we breathe
- Creates the food that sustains food chains
- Removes CO₂ from the atmosphere`;
  }
  
  // Default response
  return `## Understanding: ${prompt}

Here's what you need to know about this topic:

### Key Concepts

This is a fascinating subject with several important aspects to consider:

1. **Foundation** - Understanding the basic principles
2. **Application** - How these concepts apply in practice
3. **Implications** - The broader impact and significance

### Important Points

- The core idea centers around fundamental principles that have evolved over time
- Practical applications continue to expand as our understanding deepens
- Future developments will likely bring new insights and opportunities

### Summary

This topic connects to many other areas of knowledge and continues to be an active area of study and development.

*Feel free to ask follow-up questions to dive deeper into any specific aspect!*`;
};

export const StreamingNoteBlock = ({ 
  prompt,
  journalId,
  onStreamComplete, 
  onDelete,
  dragHandleProps,
}: StreamingNoteBlockProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  const [isStreaming, setIsStreaming] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<AnswerMetadata | null>(null);
  
  const fullTextRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);

  // Store callback in ref to avoid dependency issues
  const onStreamCompleteRef = useRef(onStreamComplete);
  onStreamCompleteRef.current = onStreamComplete;

  // Start streaming from API
  useEffect(() => {
    let isMounted = true;
    fullTextRef.current = '';
    setDisplayedText('');
    setIsStreaming(true);
    setError(null);
    setMetadata(null);

    const streamFromAPI = async () => {
      try {
        await askQuestionStreaming(
          { journal_id: journalId, question: prompt },
          (event: AskStreamEvent) => {
            if (!isMounted) return;
            
            if (event.type === 'text') {
              fullTextRef.current += event.content;
              setDisplayedText(fullTextRef.current);
            } else if (event.type === 'metadata') {
              setMetadata(event.data);
            } else if (event.type === 'complete') {
              setIsStreaming(false);
              onStreamCompleteRef.current?.(fullTextRef.current, metadata || undefined);
            } else if (event.type === 'error') {
              setError(event.message || 'Failed to generate response');
              setIsStreaming(false);
            }
          }
        );
      } catch (err) {
        if (!isMounted) return;
        console.error('Streaming failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to generate response');
        setIsStreaming(false);
      }
    };

    // Try real API first
    streamFromAPI();

    return () => {
      isMounted = false;
      abortControllerRef.current?.abort();
    };
  }, [prompt, journalId]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <div 
      className="group relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Drag handle - appears on hover */}
      <div 
        className={`absolute -left-7 top-2 transition-opacity duration-150 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div 
          className="p-1 cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-400"
          {...dragHandleProps}
        >
          <GripVertical className="w-4 h-4" />
        </div>
      </div>

      {/* Delete button - appears on hover */}
      <div 
        className={`absolute -right-7 top-2 transition-opacity duration-150 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <button
          onClick={onDelete}
          className="p-1 text-slate-300 hover:text-red-400 transition-colors"
          title="Delete note"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Note content with AI indicator */}
      <div className={`border-l-2 border-emerald-300 pl-4 transition-all duration-200 ${
        isHovered ? 'border-emerald-400' : ''
      }`}>
        {/* AI header */}
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100">
          <div className={`p-1 rounded-md ${isStreaming ? 'bg-emerald-100' : error ? 'bg-amber-100' : 'bg-slate-100'}`}>
            {isStreaming ? (
              <Loader2 className="w-3.5 h-3.5 text-emerald-600 animate-spin" />
            ) : error ? (
              <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
            ) : (
              <IconSparkles className="w-3.5 h-3.5 text-emerald-600" />
            )}
          </div>
          <span className="text-xs font-medium text-slate-400">
            {isStreaming ? 'AI is writing...' : 'AI Response'}
          </span>
          {isStreaming && displayedText.length > 0 && (
            <span className="text-xs text-slate-300">
              ({displayedText.length} chars)
            </span>
          )}
        </div>

        {/* Error state */}
        {error && !isStreaming && (
          <div className="text-sm text-red-600 py-4 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span>Failed to generate response. Please try again.</span>
          </div>
        )}

        {/* Streaming content - rendered as markdown-like */}
        {!error && (
          <div className="prose prose-sm prose-slate max-w-none">
            <div className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">
              {displayedText.split('\n').map((line, i) => {
              // Simple markdown rendering
              if (line.startsWith('## ')) {
                return <h2 key={i} className="text-lg font-semibold text-slate-800 mt-4 mb-2">{line.slice(3)}</h2>;
              }
              if (line.startsWith('### ')) {
                return <h3 key={i} className="text-base font-medium text-slate-700 mt-3 mb-1">{line.slice(4)}</h3>;
              }
              if (line.startsWith('- ')) {
                return <li key={i} className="ml-4 list-disc">{renderInlineMarkdown(line.slice(2))}</li>;
              }
              if (line.match(/^\d+\.\s/)) {
                return <li key={i} className="ml-4 list-decimal">{renderInlineMarkdown(line.replace(/^\d+\.\s/, ''))}</li>;
              }
              if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={i} className="font-semibold">{line.slice(2, -2)}</p>;
              }
              if (line.startsWith('*') && line.endsWith('*')) {
                return <p key={i} className="italic text-slate-500">{line.slice(1, -1)}</p>;
              }
              if (line.trim() === '') {
                return <br key={i} />;
              }
              return <p key={i}>{renderInlineMarkdown(line)}</p>;
            })}
              {isStreaming && <span className="animate-pulse">▊</span>}
            </div>
          </div>
        )}

        {/* Metadata display (after completion) */}
        {!error && !isStreaming && metadata && metadata.key_points.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100">
            <div className="text-xs text-slate-400 font-medium mb-1">Key Points</div>
            <ul className="text-xs text-slate-500 space-y-0.5">
              {metadata.key_points.slice(0, 3).map((point, i) => (
                <li key={i} className="flex items-start gap-1">
                  <span className="text-emerald-400">•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to render inline markdown (bold, code)
function renderInlineMarkdown(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch && boldMatch.index !== undefined) {
      if (boldMatch.index > 0) {
        parts.push(remaining.slice(0, boldMatch.index));
      }
      parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      continue;
    }
    
    // Code
    const codeMatch = remaining.match(/`(.+?)`/);
    if (codeMatch && codeMatch.index !== undefined) {
      if (codeMatch.index > 0) {
        parts.push(remaining.slice(0, codeMatch.index));
      }
      parts.push(<code key={key++} className="px-1 py-0.5 bg-slate-100 rounded text-pink-600 text-xs">{codeMatch[1]}</code>);
      remaining = remaining.slice(codeMatch.index + codeMatch[0].length);
      continue;
    }

    // No more matches
    parts.push(remaining);
    break;
  }

  return parts.length > 0 ? parts : text;
}
